import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ScrewFamily } from "../../../../http/deckDesigner/types/screwFamilies";
import { SupportFamily, UpdateSupportFamilyBody } from "../../../../http/deckDesigner/types/supportFamilies";
import useDeckScrewFamiliesApi from "../../../../http/deckDesigner/useScrewFamilies";
import useDeckSupportFamiliesApi from "../../../../http/deckDesigner/useSupportFamilies";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface SupportFamilyFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  maxLiveLoad: number | null;
  screwFamilies: ScrewFamily[];
}

interface SupportFamilyFormProps {
  supportFamily: SupportFamily;
  onClose: () => void;
}

const SupportFamilyForm: FC<SupportFamilyFormProps> = ({ supportFamily, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const supportFamiliesApi = useDeckSupportFamiliesApi();
  const screwFamiliesApi = useDeckScrewFamiliesApi();
  const screwFamilyOptions = useAsyncOptions(screwFamiliesApi.getDeckScrewFamilies);

  const defaultValues: SupportFamilyFormBody = {
    name: supportFamily.name ?? null,
    nameIt: supportFamily.nameIt ?? null,
    nameDe: supportFamily.nameDe ?? null,
    nameEs: supportFamily.nameEs ?? null,
    nameFr: supportFamily.nameFr ?? null,
    maxLiveLoad: supportFamily.maxLiveLoad ?? null,
    screwFamilies: supportFamily.screwFamilies ?? [],
  };

  const onSubmit = () => async (formData: SupportFamilyFormBody) => {
    const body: UpdateSupportFamilyBody = {
      name: formData.name ?? "",
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
      maxLiveLoad: formData.maxLiveLoad,
      screwFamilies: formData.screwFamilies,
    };

    try {
      await supportFamiliesApi.updateDeckSupportFamily(supportFamily.code, body);
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.supportFamilies.singular}`}
    >
      <Form<SupportFamilyFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth>
            <TranslatableTextField form={form} />
            <Controller
              name="maxLiveLoad"
              control={form.control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.supportFamilies.maxLiveLoad}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="screwFamilies"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.screwFamilies.plural}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId }) => (
                    <AsyncSelect<ScrewFamily, true>
                      {...screwFamilyOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SupportFamilyForm;
